import type { CaptureResult, PostHogConfig } from 'posthog-js';

export type RadarEvent = 'radar_screen_viewed' | 'radar_auth_started' | 'radar_auth_result'
  | 'radar_brief_edit_started' | 'radar_action_started' | 'radar_results_viewed'
  | 'radar_item_opened' | 'radar_item_viewed' | 'radar_copy_succeeded' | 'radar_wait_visibility_changed'
  | 'radar_api_failed' | '$exception' | 'radar_landing_viewed' | 'radar_application_clicked' | 'radar_consultation_clicked'
  | 'marketing_page_viewed' | 'marketing_cta_clicked' | 'marketing_example_selected' | 'marketing_inquiry_result'
  | 'marketing_video_started' | 'marketing_video_completed';
export type RadarProperties = {
  landing_path?: string; landing_locale?: 'en' | 'zh-Hans' | 'zh-Hant';
  page_type?: string; cta_target?: 'trial' | 'login' | 'examples' | 'service' | 'extension' | 'insights' | 'email' | 'demo';
  video_id?: string;
  scenario_id?: 'homewares' | 'industrial' | 'packaging' | 'services';
  inquiry_result?: 'accepted' | 'failed' | 'unknown';
  utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_content?: string;
  cta_position?: 'header' | 'hero' | 'footer' | 'mobile_sticky' | 'video'; consultation_channel?: 'messenger' | 'whatsapp';
  workspace_id?: string; search_run_id?: string; action_id?: string; request_id?: string;
  item_id?: string; item_type?: 'company' | 'evidence' | 'contact' | 'linkedin' | 'draft' | 'signal' | 'check_details' | 'company_email';
  company_id?: string; watch_id?: string; plan_id?: string; item_index?: number; source_index?: number;
  source_kind?: 'fit' | 'trigger' | 'signal' | 'role' | 'company_email';
  feed_group?: 'update' | 'background' | 'unconfirmed';
  screen?: 'sign_in' | 'brief_start' | 'brief' | 'discover' | 'watch' | 'act' | 'access';
  action?: string; method?: string; route?: string; status?: number; error_code?: string;
  auth_method?: 'google' | 'email'; outcome?: 'started' | 'link_sent' | 'code_sent' | 'signed_in' | 'failed' | 'unknown' | 'verified';
  auth_flow_id?: string; auth_stage?: 'send' | 'verify'; delivery_status?: 'unknown';
  collection_count?: number; new_count?: number; seen_again_count?: number;
  target?: 'email' | 'draft'; channel?: 'email' | 'linkedin';
  has_linkedin?: boolean; has_email?: boolean; draft_available?: boolean;
  is_first?: boolean; visible?: boolean; elapsed_ms?: number; wait_observed?: boolean;
  $exception_list?: { type: string; value: string; mechanism: { type: string; handled: boolean } }[];
};
type Config = { enabled: boolean; token?: string; host?: string; environment?: string; release?: string };
type Sdk = {
  init(token: string, config: Partial<PostHogConfig>): unknown;
  identify(id: string): unknown; setPersonProperties(properties: Record<string, string>): unknown; reset(resetDevice?: boolean): unknown;
  capture(event: string, properties: Record<string, unknown>): unknown;
  set_config(config: Partial<PostHogConfig>): unknown;
  startSessionRecording(options?: { sampling?: boolean }): unknown; stopSessionRecording(): unknown;
  get_property(key: string): unknown; get_session_id(): string; onSessionId(callback: (sessionId: string) => void): () => void;
};
type Context = { userId: string | null; epoch: number };

const eventNames = new Set<RadarEvent>(['radar_screen_viewed', 'radar_auth_started', 'radar_auth_result',
  'radar_brief_edit_started', 'radar_action_started', 'radar_results_viewed', 'radar_item_opened', 'radar_item_viewed',
  'radar_copy_succeeded', 'radar_wait_visibility_changed', 'radar_api_failed', '$exception',
  'radar_landing_viewed', 'radar_application_clicked', 'radar_consultation_clicked',
  'marketing_page_viewed', 'marketing_cta_clicked', 'marketing_example_selected', 'marketing_inquiry_result', 'marketing_video_started', 'marketing_video_completed']);
const landingEvents = new Set<RadarEvent>(['radar_landing_viewed', 'radar_application_clicked', 'radar_consultation_clicked',
  'marketing_page_viewed', 'marketing_cta_clicked', 'marketing_example_selected', 'marketing_inquiry_result', 'marketing_video_started', 'marketing_video_completed']);
const landingProperties = new Set(['landing_path', 'landing_locale', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'cta_position', 'consultation_channel',
  'page_type', 'cta_target', 'scenario_id', 'inquiry_result', 'video_id']);
const safeProperties = new Set(['workspace_id', 'search_run_id', 'action_id', 'request_id', 'item_id',
  'company_id', 'watch_id', 'plan_id', 'item_index', 'source_index', 'source_kind', 'feed_group',
  'item_type', 'screen', 'action', 'method', 'route', 'status', 'error_code', 'auth_method', 'outcome',
  'auth_flow_id', 'auth_stage', 'delivery_status',
  'collection_count', 'new_count', 'seen_again_count', 'target', 'channel', 'has_linkedin', 'has_email',
  'draft_available', 'is_first', 'visible', 'elapsed_ms', 'wait_observed', '$exception_list', 'schema_version',
  // The public project token is required by PostHog ingestion, not an auth credential.
  'token', 'source', 'environment', 'analytics_cohort', 'app_release', 'distinct_id', '$user_id', '$anon_distinct_id', '$device_id',
  '$session_id', '$window_id', '$lib', '$lib_version', '$browser', '$browser_version', '$os', '$os_version',
  '$device_type', '$screen_height', '$screen_width', '$viewport_height', '$viewport_width',
  '$is_identified', '$process_person_profile', '$time', '$insert_id']);
/** Also remove implicit SDK URL/referrer/person properties, not only explicit payloads. */
export function sanitizeRadarCapture(event: CaptureResult | null): CaptureResult | null {
  if (!event || (!eventNames.has(event.event as RadarEvent) && !['$identify', '$set', '$snapshot'].includes(event.event))) return null;
  const cohort = event.properties.$set?.analytics_cohort;
  event.properties = Object.fromEntries(Object.entries(event.properties).filter(([key]) =>
    safeProperties.has(key) || (landingEvents.has(event.event as RadarEvent) && landingProperties.has(key)) || (event.event === '$snapshot' && key.startsWith('$snapshot'))));
  if (['pilot', 'internal', 'unknown'].includes(cohort)) event.properties.$set = { analytics_cohort: cohort };
  return event;
}

/** Local controls override project toggles: no business text or transport payloads. */
export function radarPostHogConfig(host: string): Partial<PostHogConfig> {
  return {
    before_send: sanitizeRadarCapture, api_host: host, persistence: 'localStorage', person_profiles: 'identified_only',
    autocapture: false, capture_pageview: false, capture_pageleave: false,
    capture_exceptions: false, capture_dead_clicks: false, capture_heatmaps: false,
    capture_performance: false, rageclick: false, disable_surveys: true,
    advanced_disable_feature_flags: true, enable_recording_console_log: false,
    disable_session_recording: true, ip: false,
    save_referrer: false, save_campaign_params: false, disable_capture_url_hashes: true,
    property_blacklist: ['$current_url', '$pathname', '$host', '$referrer', '$referring_domain',
      '$initial_current_url', '$initial_pathname', '$initial_referrer', '$initial_referring_domain',
      '$initial_utm_source', '$initial_utm_medium', '$initial_utm_campaign', '$initial_utm_content', '$initial_utm_term'],
    session_recording: {
      sampleRate: 1, maskAllInputs: true, maskTextSelector: '.radar-private, .radar-private *',
      blockSelector: 'input[type="hidden"], input[type="file"], iframe, .ph-no-capture',
      // URLs, titles, aria labels and data attributes can contain private names.
      maskAttributeFn: (name, value) => ['class', 'style', 'type', 'disabled', 'hidden', 'role', 'width', 'height'].includes(name) ? value : '[masked]',
      recordHeaders: false, recordBody: false, captureJsonLd: false,
      captureCanvas: { recordCanvas: false },
      // The SDK also calls this for replay page metadata with only {name}.
      // Keep a fixed page URL so replay has its required Meta event.
      maskCapturedNetworkRequestFn: data => Object.keys(data).length === 1 && typeof data.name === 'string'
        ? { ...data, name: 'https://radar.invalid/buyer-radar/app' } : null,
    },
  };
}

export function createRadarAnalytics(config: Config, load: () => Promise<Sdk>) {
  let sdk: Sdk | null = null;
  let loading: Promise<void> | null = null;
  let userId: string | null = null;
  let appliedUser: string | null = null;
  let admitted = false;
  let cohort: 'pilot' | 'internal' | 'unknown' = 'unknown';
  let appliedCohort = '';
  let recording = false;
  let active = false;
  let surface: 'app' | 'landing' = 'app';
  let epoch = 0;
  let lastScreen = '';
  let changingIdentity = false;
  let knownSession: string | undefined;
  const seen = new Set<string>();
  const listeners = new Set<() => void>();
  const waits = new Map<string, { startedAt: number; runId?: string; workspaceId: string }>();
  let stopSessionListener: (() => void) | undefined;
  const publish = () => listeners.forEach(listener => { try { listener(); } catch { /* Optional observer. */ } });
  const safe = (fn: () => void) => { try { fn(); } catch { /* Optional analytics cannot block the product. */ } };

  function applyIdentity() {
    if (!sdk) return;
    changingIdentity = true;
    safe(() => {
      if (appliedUser !== userId) {
        sdk!.stopSessionRecording(); recording = false;
        if (appliedUser !== null) sdk!.reset(true);
        if (userId) sdk!.identify(userId);
        appliedUser = userId; appliedCohort = '';
      }
      if (userId && appliedCohort !== cohort) { sdk!.setPersonProperties({ analytics_cohort: cohort }); appliedCohort = cohort; }
      const shouldRecord = active && surface === 'app' && admitted && !!userId;
      if (shouldRecord !== recording) {
        sdk!.set_config({ disable_session_recording: !shouldRecord });
        if (shouldRecord) sdk!.startSessionRecording({ sampling: true });
        else sdk!.stopSessionRecording();
        recording = shouldRecord;
      }
    });
    changingIdentity = false;
  }
  async function start(nextSurface: 'app' | 'landing' = 'app') {
    surface = nextSurface;
    if (surface === 'landing') {
      if (userId !== null) { epoch++; seen.clear(); waits.clear(); }
      userId = null; admitted = false; cohort = 'unknown';
    }
    active = true;
    if (!config.enabled || !config.token || !config.host || typeof window === 'undefined') return;
    if (!loading) loading = load().then(loaded => {
      loaded.init(config.token!, radarPostHogConfig(config.host!)); sdk = loaded;
      const persistedUser = sdk.get_property('$user_id');
      appliedUser = typeof persistedUser === 'string' ? persistedUser : null;
      stopSessionListener = sdk.onSessionId(nextSession => {
        if (knownSession && knownSession !== nextSession) waits.clear();
        knownSession = nextSession; publish();
      });
      applyIdentity(); publish();
    }).catch(() => { loading = null; /* A later start may retry a failed load. */ });
    await loading; applyIdentity();
  }
  function identity(nextUser: string | null, nextAdmitted: boolean, nextCohort?: string) {
    if (surface === 'landing') return;
    if (userId !== nextUser) { epoch++; seen.clear(); waits.clear(); }
    const nextGroup = nextAdmitted && ['pilot', 'internal'].includes(nextCohort || '') ? nextCohort as 'pilot' | 'internal' : 'unknown';
    const changed = userId !== nextUser || admitted !== nextAdmitted || cohort !== nextGroup;
    userId = nextUser; admitted = nextAdmitted; cohort = nextGroup;
    applyIdentity(); if (changed) publish();
  }
  function context(): Context { return { userId, epoch }; }
  function sessionId(): string | undefined {
    if (!sdk || !active || !userId) return undefined;
    try {
      const nextSession = sdk.get_session_id();
      if (knownSession && knownSession !== nextSession) waits.clear();
      knownSession = nextSession;
      return nextSession;
    } catch { return undefined; }
  }
  function capture(event: RadarEvent, properties: RadarProperties = {}, expected = context()): boolean {
    if (!sdk || !active || changingIdentity || appliedUser !== userId || expected.epoch !== epoch || expected.userId !== userId) return false;
    const isLanding = landingEvents.has(event);
    if (isLanding !== (surface === 'landing')) return false;
    if (!isLanding && !admitted && !['radar_auth_started', 'radar_auth_result', 'radar_api_failed', '$exception'].includes(event)
      && !(event === 'radar_screen_viewed' && ['sign_in', 'access'].includes(properties.screen || ''))) return false;
    try {
      sdk.capture(event, { ...properties, schema_version: 1, source: 'browser',
        environment: config.environment || 'unknown', analytics_cohort: cohort, app_release: config.release || 'unknown' });
      return true;
    } catch { return false; }
  }
  function once(event: RadarEvent, key: string, properties: RadarProperties): boolean {
    const session = sessionId(); if (!userId || !session) return false;
    const scopedKey = `radar-analytics:1:${userId}:${session}:${event}:${key}`;
    let saved = false; safe(() => { saved = sessionStorage.getItem(scopedKey) === '1'; });
    if (seen.has(scopedKey) || saved) return false;
    if (!capture(event, properties)) return false;
    seen.add(scopedKey); safe(() => sessionStorage.setItem(scopedKey, '1'));
    return true;
  }
  function resultsViewed(properties: RadarProperties & { workspace_id: string; search_run_id: string }): boolean {
    sessionId(); // Rotate stale waits before looking up this run.
    const entry = [...waits.entries()].find(([, item]) => item.runId === properties.search_run_id && item.workspaceId === properties.workspace_id);
    const wait = entry?.[1];
    const captured = once('radar_results_viewed', `${properties.workspace_id}:${properties.search_run_id}`, {
      ...properties, wait_observed: !!wait, ...(wait ? { elapsed_ms: Math.round(performance.now() - wait.startedAt) } : {}),
    });
    if (captured && entry) waits.delete(entry[0]);
    return captured;
  }

  return {
    start, identity, context, sessionId, capture, once, resultsViewed,
    screenViewed(screen: RadarProperties['screen'], workspaceId?: string) {
      const key = `${userId}:${sessionId()}:${workspaceId}:${screen}`;
      if (lastScreen === key) return;
      if (capture('radar_screen_viewed', { screen, workspace_id: workspaceId })) lastScreen = key;
    },
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    stop() { active = false; epoch++; waits.clear(); applyIdentity(); },
    dispose() { active = false; applyIdentity(); stopSessionListener?.(); listeners.clear(); },
    beginSearch(actionId: string, workspaceId: string) {
      sessionId();
      if (!waits.has(actionId)) waits.set(actionId, { startedAt: performance.now(), workspaceId });
    },
    waitElapsed(runId: string) {
      const wait = [...waits.values()].find(item => item.runId === runId);
      return wait ? Math.round(performance.now() - wait.startedAt) : undefined;
    },
    bindSearch(actionId: string, runId: string, expected = context()) {
      if (expected.epoch !== epoch || expected.userId !== userId) return;
      const wait = waits.get(actionId); if (wait) wait.runId = runId;
    },
  };
}
export const radarAnalytics = createRadarAnalytics({
  enabled: import.meta.env.VITE_ANALYTICS_ENABLED === 'true' && !import.meta.env.SSR,
  token: import.meta.env.VITE_POSTHOG_PROJECT_TOKEN, host: import.meta.env.VITE_POSTHOG_HOST,
  environment: import.meta.env.VITE_ANALYTICS_ENVIRONMENT || import.meta.env.MODE, release: import.meta.env.VITE_APP_RELEASE,
}, async () => (await import('posthog-js')).default);

/** Hidden mounted panels and offscreen content never count as a view. */
export function observeVisible(element: HTMLElement, onVisible: () => void): () => void {
  function check() {
    if (document.visibilityState === 'hidden' || !element.isConnected || element.closest('[hidden]')) return;
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      if (parent.tagName === 'DETAILS' && !parent.hasAttribute('open')
        && !parent.querySelector(':scope > summary')?.contains(element)) return;
    }
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return;
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight
      && rect.right > 0 && rect.left < window.innerWidth) onVisible();
  }
  const observer = typeof IntersectionObserver === 'undefined' ? undefined : new IntersectionObserver(check);
  observer?.observe(element);
  document.addEventListener('visibilitychange', check);
  document.addEventListener('toggle', check, true);
  window.addEventListener('scroll', check, true); window.addEventListener('resize', check);
  const unsubscribe = radarAnalytics.subscribe(check); check();
  return () => { observer?.disconnect(); unsubscribe(); document.removeEventListener('visibilitychange', check);
    document.removeEventListener('toggle', check, true);
    window.removeEventListener('scroll', check, true); window.removeEventListener('resize', check); };
}
export function radarBrowserException(kind: string, mechanism = 'onerror') {
  radarAnalytics.capture('$exception', { $exception_list: [{
    type: ['TypeError', 'RangeError', 'ReferenceError', 'SyntaxError'].includes(kind) ? kind : 'Error',
    value: 'Buyer Radar browser error', mechanism: { type: mechanism, handled: false },
  }] });
}
